import type { ReactNode } from "react";
import { QRCodeSVG } from "qrcode.react";
import LegacyTable, { HeaderCell } from "@core/components/legacy-table";
import { Badge, Text, Title } from "rizzui";
import { CaveatBanner } from "./caveat-banner";
import { ComplianceStatusBadge, type ComplianceState } from "./compliance-status-badge";

/**
 * The statutory GST invoice, laid out from the API's own field values (`006` FR-011, T034 Step 0).
 *
 * ## Corrected understanding of `GET /admin/invoices/{id}/print`
 *
 * The earlier version of this component expected the API to answer with pre-rendered HTML (a
 * `{ html: string }` contract) and to hand that markup through unmodified via
 * `dangerouslySetInnerHTML`. Verified against the real backend, that contract does not exist:
 * `AdminInvoiceController.PrintInvoice` and `GetInvoice` both call the same
 * `InvoicePrintHandler.Render(invoice)` and return `InvoicePrint` — a flat field record
 * (`invoiceNumber`, `lines[]`, `taxableValue`, `cgst`/`sgst`/`igst`, `grandTotal`, `irn`, `irnQr`,
 * `irnStatus`, `state`, …), never markup. "A read and a render, with no arithmetic" (the handler's
 * own doc comment) means the API renders *values*, not a *document* — laying the values into a
 * document is this component's job, not something to re-derive from a mythical HTML field.
 *
 * This component still does not **compute** anything (Principle I stands): every figure printed is
 * a field already on `InvoiceDocumentData`, or one of the API's own `…Display` strings
 * (`grandTotalDisplay`, `lineTotalDisplay`). No tax is summed, no rate is multiplied out, no
 * quantity is subtracted from another. `cancelled` and `irnState` are computed by the caller from
 * the same wire vocabulary the invoice list already maps (`IRN_STATE`, `isCancelled` in
 * `apps/admin/src/lib/services/invoicing.ts`) so that mapping has exactly one implementation.
 *
 * ## No placeholders, ever
 *
 * IRN and its signed QR payload print when present and leave nothing behind when absent (FR-012).
 * A missing GSTIN prints as "not recorded"/"none", the same pattern `Detail.tsx` already used for
 * absent GSTINs (FR-013) — never a dash, never "N/A".
 *
 * ## The print stylesheet is a separate import
 *
 * `apps/admin/src/print.css`, imported by the print route's own chrome-free layout, keyed off this
 * component's `data-slot="invoice-document"` and plain `<table>/<th>/<td>` markup (via
 * `@core/components/legacy-table`, which renders real table elements through `rc-table`).
 */
export type InvoiceDocumentLine = {
  id: string;
  skuName: string;
  brandName: string;
  categoryName: string;
  hsnCode: string;
  gstRate: number | string;
  quantity: number | string;
  unitCode: string;
  taxableValue: number | string;
  taxAmount: number | string;
  lineTotalDisplay: string;
  /** D-39: quantity × rate before the trade discount — absent on a document raised before discounts existed. */
  grossAmount?: number | string | null;
  /** D-39: the trade discount percentage, as Billing copied it onto the line. */
  discountPercent?: number | string | null;
  /** D-39: the line's trade discount. Billing stores `grossAmount − discount = taxableValue`. */
  discount?: number | string | null;
};

export type InvoiceDocumentData = {
  invoiceNumber: string;
  financialYear: string;
  /** Plain ISO date, e.g. `2026-09-10`. */
  invoiceDate: string;
  supplierGstin: string | null;
  recipientGstin: string | null;
  recipientLegalName: string;
  recipientAddress: string;
  placeOfSupply: string;
  invoiceBasis: string;
  state: string;
  irnStatus: string;
  irn: string | null;
  /** The signed QR payload — printed verbatim, like the IRN, and drawn as a scannable QR beside it. */
  irnQr?: string | null;
  /** The company's own name and address as Billing recorded them; absent on an invoice raised before they were kept. */
  supplierLegalName?: string | null;
  supplierAddress?: string | null;
  /** The e-way bill (§13.3) — only what the API sent, never computed. Every field prints only when present. */
  ewayBillNo?: string | null;
  ewayBillStatus?: string | null;
  ewayBillDate?: string | null;
  ewayBillValidUntil?: string | null;
  lines: InvoiceDocumentLine[];
  taxableValue: number | string;
  cgst: number | string;
  sgst: number | string;
  igst: number | string;
  roundOff: number | string;
  /** Indian-grouped, already formatted by the API (`IndianCurrency`). */
  grandTotalDisplay: string;
  /** D-39: the lines' gross before the trade discount, as Billing stored it. */
  grossValue?: number | string | null;
  /** D-39: the trade discount, taken off before GST. Absent or zero means none — nothing then prints. */
  discount?: number | string | null;
  /** D-31: the order's delivery charge, invoiced untaxed — in the grand total, not the taxable value. Absent or zero prints nothing. */
  deliveryCharge?: number | string | null;
};

/** `Invoice.Basis`'s two wire values (BR-02/BR-03) — fixed vocabulary, not app-specific. */
const BASIS_LABEL: Readonly<Record<string, string>> = {
  actual_measured_quantity: "Actual measured quantity at packing",
  actual_piece_count: "Actual piece count",
};

const BASIS_RULE: Readonly<Record<string, string>> = {
  actual_measured_quantity:
    "BR-03 — a B2B invoice is raised on the quantity actually measured at packing, at the tier unit rate. The quantity invoiced is not the quantity ordered.",
  actual_piece_count:
    "BR-02 — a B2C invoice is raised on the actual piece count at the piece price. The weight recorded at packing is on the record and did not affect this amount.",
};

const ABSENT_QUANTITY_BASIS =
  "Ordered quantity and pack weight aren't shown here. What's shown is the invoiced quantity and the basis it was raised on.";

function toNumber(value: number | string): number {
  return typeof value === "number" ? value : Number(value);
}

// Indian digit grouping, display only — every figure here already arrived rounded by the API
// (constitution I). Kept local rather than imported from an app's lib: this component is shared
// across apps (back office today; portal and 012's customer app per T006) and must not depend on
// any one app's folder, and `Intl`'s `en-IN` formatting rule will never itself drift between copies.
const MONEY = new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR" });
function money(value: number | string): string {
  return MONEY.format(toNumber(value));
}

/**
 * D-39: whether the API reported a trade discount on this document. A read of its own figure, not
 * a derivation — a document with none (every one raised before D-39) prints exactly as it always did.
 */
function hasDeliveryCharge(data: InvoiceDocumentData): boolean {
  return data.deliveryCharge !== undefined && data.deliveryCharge !== null && toNumber(data.deliveryCharge) > 0;
}

function hasDiscount(data: InvoiceDocumentData): boolean {
  return data.discount !== undefined && data.discount !== null && toNumber(data.discount) > 0;
}

/** A line's discount with the percentage it came from, e.g. "₹1,266.16 (5%)". Both figures are the API's. */
function discountCell(line: InvoiceDocumentLine): string {
  const amount = money(line.discount ?? 0);
  return line.discountPercent !== undefined && line.discountPercent !== null && toNumber(line.discountPercent) > 0
    ? `${amount} (${String(line.discountPercent)}%)`
    : amount;
}

function formatDate(iso: string): string {
  return new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "short", year: "numeric", timeZone: "UTC" }).format(new Date(iso));
}

// An e-way bill's validity is a moment, not a day — printed in the business's clock (the deployment's
// VITE_BUSINESS_TIME_ZONE, India by default; the same value as lib/format.ts, which theme/ may not import).
function formatDateTime(iso: string): string {
  return new Intl.DateTimeFormat("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    timeZone: import.meta.env.VITE_BUSINESS_TIME_ZONE || "Asia/Kolkata",
  }).format(new Date(iso));
}

/** QR version 40 at level L holds 2,953 bytes; a longer payload is left as text rather than throwing the page. */
const QR_MAX_LENGTH = 2900;

function statusWords(status: string): string {
  return status.replaceAll("_", " ");
}

/** D-39's two columns, inserted after Unit only when the document carries a trade discount. */
const discountColumns = [
  {
    title: <HeaderCell title="Amount" align="right" />,
    dataIndex: "grossAmount",
    key: "grossAmount",
    render: (_: unknown, line: InvoiceDocumentLine) => (
      <span className="block text-right tabular-nums">{money(line.grossAmount ?? line.taxableValue)}</span>
    ),
  },
  {
    title: <HeaderCell title="Discount" align="right" />,
    dataIndex: "discount",
    key: "discount",
    render: (_: unknown, line: InvoiceDocumentLine) => <span className="block text-right tabular-nums">{discountCell(line)}</span>,
  },
];

function columnsFor(discounted: boolean) {
  if (!discounted) return lineColumns;
  const afterUnit = lineColumns.findIndex((column) => column.key === "unitCode") + 1;
  return [...lineColumns.slice(0, afterUnit), ...discountColumns, ...lineColumns.slice(afterUnit)];
}

const lineColumns = [
  {
    title: "Item",
    dataIndex: "skuName",
    key: "skuName",
    render: (_: string, line: InvoiceDocumentLine) => (
      <>
        <span className="font-medium">{line.skuName}</span>
        <span className="text-muted-foreground block text-xs">
          {line.brandName} · {line.categoryName}
        </span>
      </>
    ),
  },
  {
    title: "HSN",
    dataIndex: "hsnCode",
    key: "hsnCode",
    render: (value: string) => <span className="font-mono text-xs">{value}</span>,
  },
  {
    title: <HeaderCell title="GST rate" align="right" />,
    dataIndex: "gstRate",
    key: "gstRate",
    render: (value: InvoiceDocumentLine["gstRate"]) => <span className="block text-right tabular-nums">{String(value)}%</span>,
  },
  {
    title: <HeaderCell title="Invoiced qty" align="right" />,
    dataIndex: "quantity",
    key: "quantity",
    render: (value: InvoiceDocumentLine["quantity"]) => <span className="block text-right tabular-nums">{String(value)}</span>,
  },
  {
    title: <HeaderCell title="Unit" align="right" />,
    dataIndex: "unitCode",
    key: "unitCode",
    render: (value: string) => <span className="block text-right">{value}</span>,
  },
  {
    title: <HeaderCell title="Taxable value" align="right" />,
    dataIndex: "taxableValue",
    key: "taxableValue",
    render: (value: InvoiceDocumentLine["taxableValue"]) => <span className="block text-right tabular-nums">{money(value)}</span>,
  },
  {
    title: <HeaderCell title="Tax" align="right" />,
    dataIndex: "taxAmount",
    key: "taxAmount",
    render: (value: InvoiceDocumentLine["taxAmount"]) => <span className="block text-right tabular-nums">{money(value)}</span>,
  },
  {
    title: <HeaderCell title="Line total" align="right" />,
    dataIndex: "lineTotalDisplay",
    key: "lineTotalDisplay",
    render: (value: string) => <span className="block text-right tabular-nums">{value}</span>,
  },
];

/**
 * The two headings this component draws internally (document title, then section) at whichever
 * level fits the caller's own heading outline (WCAG 1.3.1 — no level skipped).
 *
 * `Print.tsx` renders this with no ancestor heading at all (own top-level route, no `PageHeader`),
 * so its document title is the page's actual `h1` and its sections are `h2`. `Detail.tsx` renders
 * it under `PageHeader`'s own `h2` title, so nesting one level down makes the document title `h3`
 * and its sections `h4`.
 */
const TITLE_TAG = { 1: "h1", 3: "h3" } as const;
const SECTION_TAG = { 1: "h2", 3: "h4" } as const;

export function InvoiceDocument({
  data,
  /** Computed by the caller from its own `isCancelled` — kept a single implementation. */
  cancelled,
  /** Computed by the caller from its own `IRN_STATE` map; `undefined` falls back to the raw word. */
  irnState,
  /** For a screen reader and the browser's document outline. */
  label = "Tax invoice",
  /** See `TITLE_TAG` above. Defaults to the nested-in-a-page case (`Detail.tsx`'s). */
  headingLevel = 3,
  /** Print controls, back links — anything that must not reach paper. Marked no-print. */
  children,
}: {
  data: InvoiceDocumentData;
  cancelled: boolean;
  irnState?: ComplianceState;
  label?: string;
  headingLevel?: keyof typeof TITLE_TAG;
  children?: ReactNode;
}) {
  const basisRule = BASIS_RULE[data.invoiceBasis];
  const discounted = hasDiscount(data);
  const titleTag = TITLE_TAG[headingLevel];
  const sectionTag = SECTION_TAG[headingLevel];

  return (
    <>
      {children ? (
        <div data-print="hide" className="flex flex-wrap gap-2 p-4">
          {children}
        </div>
      ) : null}

      <article
        data-slot="invoice-document"
        aria-label={label}
        className="w-full rounded-xl border border-muted p-5 text-sm sm:p-6 lg:p-8"
      >
        <div className="mb-10 flex flex-col-reverse items-start justify-between gap-4 md:flex-row">
          <div>
            <Title as={titleTag}>{data.supplierLegalName ?? "Infra Junction"}</Title>
            <Text className="mt-0.5 text-gray-500">GST tax invoice</Text>
          </div>
          <div>
            <div className="mb-3 md:mb-2">
              {cancelled ? (
                <Badge variant="flat" color="danger" rounded="md">
                  Cancelled
                </Badge>
              ) : irnState ? (
                <ComplianceStatusBadge subject="irn" state={irnState} />
              ) : (
                <Badge variant="outline" rounded="md">
                  {statusWords(data.irnStatus)}
                </Badge>
              )}
            </div>
            <Title as={titleTag}>{data.invoiceNumber}</Title>
            <Text className="mt-0.5 text-gray-500">Invoice number · FY {data.financialYear}</Text>
          </div>
        </div>

        <div className="mb-10 grid gap-6 sm:grid-cols-2">
          <div>
            <Title as={sectionTag} className="mb-3.5 font-semibold">
              Supplier
            </Title>
            {data.supplierLegalName ? (
              <Text className="mb-1.5 text-sm font-semibold uppercase">{data.supplierLegalName}</Text>
            ) : null}
            {data.supplierAddress ? <Text className="mb-4">{data.supplierAddress}</Text> : null}
            <div className="mb-4">
              <Text className="mb-2 text-sm font-semibold">GSTIN</Text>
              <Text>
                {data.supplierGstin ? (
                  data.supplierGstin
                ) : (
                  <Text as="span" className="italic text-gray-500">
                    not recorded — awaiting the company GSTIN
                  </Text>
                )}
              </Text>
            </div>
            <div>
              <Text className="mb-2 text-sm font-semibold">Invoice date</Text>
              <Text>{formatDate(data.invoiceDate)}</Text>
            </div>
          </div>

          <div>
            <Title as={sectionTag} className="mb-3.5 font-semibold">
              Bill to
            </Title>
            <Text className="mb-1.5 text-sm font-semibold uppercase">{data.recipientLegalName}</Text>
            <Text className="mb-4 sm:mb-6 md:mb-8">{data.recipientAddress}</Text>
            <div className="mb-4">
              <Text className="mb-2 text-sm font-semibold">GSTIN</Text>
              <Text>
                {data.recipientGstin ? (
                  data.recipientGstin
                ) : (
                  <Text as="span" className="italic text-gray-500">
                    none — unregistered recipient
                  </Text>
                )}
              </Text>
            </div>
            <div>
              <Text className="mb-2 text-sm font-semibold">Place of supply</Text>
              <Text>{data.placeOfSupply}</Text>
            </div>
          </div>
        </div>

        <CaveatBanner caveat={ABSENT_QUANTITY_BASIS}>
          <LegacyTable
            data={data.lines}
            caption={<span className="sr-only">Invoice lines with HSN, GST rate, invoiced quantity, taxable value and tax</span>}
            // @ts-expect-error — `lineColumns`' `render` fns take the real `InvoiceDocumentLine` row,
            // narrower than rc-table's `DefaultRecordType` (`Record<string, any>`).
            columns={columnsFor(discounted)}
            variant="modern"
            rowKey={(record) => record.id}
            scroll={{ x: 900 }}
            className="mb-8"
          />
        </CaveatBanner>

        <div className="flex flex-col-reverse items-start justify-between gap-6 border-t border-muted pt-8 sm:flex-row">
          <div className="grid max-w-md gap-4 pe-4">
            <Title as={sectionTag} className="mb-1 text-xs font-semibold uppercase xs:mb-2 xs:text-sm">
              Compliance
            </Title>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Text className="mb-2 text-sm font-semibold">State</Text>
                <Text>{statusWords(data.state)}</Text>
              </div>
              <div>
                <Text className="mb-2 text-sm font-semibold">IRN</Text>
                <Text>
                  {data.irn ? (
                    data.irn
                  ) : (
                    <Text as="span" className="italic text-gray-500">
                      none
                    </Text>
                  )}
                </Text>
              </div>
              <div>
                <Text className="mb-2 text-sm font-semibold">Quantity basis</Text>
                <Text>{BASIS_LABEL[data.invoiceBasis] ?? statusWords(data.invoiceBasis)}</Text>
              </div>
            </div>
            {basisRule ? <Text className="text-xs text-gray-500">{basisRule}</Text> : null}
            {data.ewayBillNo ? (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Text className="mb-2 text-sm font-semibold">E-way bill</Text>
                  <Text>{data.ewayBillNo}</Text>
                </div>
                {data.ewayBillStatus ? (
                  <div>
                    <Text className="mb-2 text-sm font-semibold">E-way bill status</Text>
                    <Text>{statusWords(data.ewayBillStatus)}</Text>
                  </div>
                ) : null}
                {data.ewayBillDate ? (
                  <div>
                    <Text className="mb-2 text-sm font-semibold">E-way bill date</Text>
                    <Text>{formatDateTime(data.ewayBillDate)}</Text>
                  </div>
                ) : null}
                {data.ewayBillValidUntil ? (
                  <div>
                    <Text className="mb-2 text-sm font-semibold">E-way bill valid until</Text>
                    <Text>{formatDateTime(data.ewayBillValidUntil)}</Text>
                  </div>
                ) : null}
              </div>
            ) : null}
            {data.irnQr ? (
              <div>
                <Text className="mb-2 text-sm font-semibold">Signed QR payload</Text>
                {data.irnQr.length <= QR_MAX_LENGTH ? (
                  <QRCodeSVG value={data.irnQr} size={128} role="img" aria-label="IRN QR code" className="mb-2" />
                ) : null}
                <Text className="font-mono text-xs break-all">{data.irnQr}</Text>
              </div>
            ) : null}
          </div>
          <div className="w-full max-w-sm">
            {discounted ? (
              <>
                <Text className="flex items-center justify-between border-b border-muted pb-3.5 lg:pb-5">
                  Gross value{" "}
                  <Text as="span" className="font-semibold">
                    {money(data.grossValue ?? data.taxableValue)}
                  </Text>
                </Text>
                <Text className="flex items-center justify-between border-b border-muted py-3.5 lg:py-5">
                  Less trade discount{" "}
                  <Text as="span" className="font-semibold">
                    {money(data.discount ?? 0)}
                  </Text>
                </Text>
              </>
            ) : null}
            <Text className={discounted ? "flex items-center justify-between border-b border-muted py-3.5 lg:py-5" : "flex items-center justify-between border-b border-muted pb-3.5 lg:pb-5"}>
              Taxable value{" "}
              <Text as="span" className="font-semibold">
                {money(data.taxableValue)}
              </Text>
            </Text>
            <Text className="flex items-center justify-between border-b border-muted py-3.5 lg:py-5">
              CGST{" "}
              <Text as="span" className="font-semibold">
                {money(data.cgst)}
              </Text>
            </Text>
            <Text className="flex items-center justify-between border-b border-muted py-3.5 lg:py-5">
              SGST{" "}
              <Text as="span" className="font-semibold">
                {money(data.sgst)}
              </Text>
            </Text>
            <Text className="flex items-center justify-between border-b border-muted py-3.5 lg:py-5">
              IGST{" "}
              <Text as="span" className="font-semibold">
                {money(data.igst)}
              </Text>
            </Text>
            {hasDeliveryCharge(data) ? (
              <Text className="flex items-center justify-between border-b border-muted py-3.5 lg:py-5">
                Delivery charge (no GST){" "}
                <Text as="span" className="font-semibold">
                  {money(data.deliveryCharge ?? 0)}
                </Text>
              </Text>
            ) : null}
            <Text className="flex items-center justify-between border-b border-muted py-3.5 lg:py-5">
              Round off{" "}
              <Text as="span" className="font-semibold">
                {money(data.roundOff)}
              </Text>
            </Text>
            <Text className="flex items-center justify-between pt-4 text-base font-semibold text-gray-900 lg:pt-5">
              Grand total <Text as="span">{data.grandTotalDisplay}</Text>
            </Text>
          </div>
        </div>
      </article>
    </>
  );
}
